import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isDeleted", "isUnpublished", "readOnly", "required", "value"];
import { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import { userInfoSync } from 'hub-http/userInfo';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { debounce, isEqual } from 'underscore';
import UIAlert from 'UIComponents/alert/UIAlert';
import Small from 'UIComponents/elements/Small';
import * as hubDbSelectors from 'ContentData/selectors/hubDb';
import * as HubDbActions from 'ContentData/actions/HubDb';
import { DEFAULT_PAGE_SIZE, ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/cmv2/constants';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import EditorTypes from 'ContentUtils/constants/EditorTypes';
import Routes from 'ContentUtils/Routes';
import { HOST_TEMPLATE_TYPES } from 'ContentComponents/cmv2/constants';
import * as ModuleContentTypes from 'ContentUtils/constants/ModuleContentTypes';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.hubdb.${key}`;
const mapStateToProps = (state, ownProps) => {
  return {
    hubDbTables: hubDbSelectors.getHubDbTables(state),
    isDeleted: hubDbSelectors.getIsDeleted(state, {
      id: ownProps.value
    }),
    isUnpublished: hubDbSelectors.getIsUnpublished(state, {
      id: ownProps.value
    }),
    defaultOptions: hubDbSelectors.getDefaultHubDbTableOptions(state),
    options: hubDbSelectors.getHubDbTableOptions(state),
    optionsRequestStatus: hubDbSelectors.getHubDbCacheRequestStatus(state)
  };
};
const mapDispatchToProps = {
  fetchHubDbTable: HubDbActions.fetchHubDbTable,
  fetchHubDbTables: HubDbActions.fetchHubDbTables,
  saveCache: HubDbActions.saveCache,
  clearCache: HubDbActions.clearCache
};
const connector = connect(mapStateToProps, mapDispatchToProps);
class HubDbField extends Component {
  constructor(props) {
    super(props);
    const {
      user
    } = userInfoSync();
    this.hasHubDbAccess = user.scopes.includes('hubdb-schemas-read') && user.scopes.includes('hubdb-data-read');
    this.hasHubDbPageAccess = user.scopes.includes('hubdb-page-template-access');
    this.hasHubDbEmailAccess = user.scopes.includes('hubdb-email-template-access');
    this.onChange = this.onChange.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.debouncedLoadOptions = debounce(this.loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this.state = {
      input: ''
    };
  }
  componentDidMount() {
    const {
      fetchHubDbTable,
      value
    } = this.props;
    if (value) {
      fetchHubDbTable(value).catch(err => {
        throw err;
      });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      fetchHubDbTable,
      hubDbTables,
      value
    } = this.props;
    const {
      value: prevValue
    } = prevProps;
    if (!isEqual(prevValue, value) && !hubDbTables[value]) {
      fetchHubDbTable(value).catch(err => {
        throw err;
      });
    }
  }
  get isInContentEditor() {
    const {
      editor
    } = this.props;
    return editor !== EditorTypes.GLOBAL_CONTENT && editor !== EditorTypes.EMAIL && editor !== EditorTypes.DESIGN_MANAGER;
  }
  get isMissingEmailPermissions() {
    const {
      editor,
      isEmailOrAnyType
    } = this.props;
    return Routes.isUngated('HubDb:EnforceScopeValidations') && (editor === EditorTypes.EMAIL || editor === EditorTypes.DESIGN_MANAGER && isEmailOrAnyType) && !this.hasHubDbEmailAccess;
  }
  get isMissingContentPermissions() {
    const {
      editor
    } = this.props;
    return Routes.isUngated('HubDb:EnforceScopeValidations') && (this.isInContentEditor || editor === EditorTypes.DESIGN_MANAGER && this.isInContentHostTemplateType) && !this.hasHubDbPageAccess;
  }
  get isInContentHostTemplateType() {
    const {
      contentTypes,
      hostTemplateTypes
    } = this.props;
    return Routes.isUngated('CMv2:UseContentTypes') ? contentTypes && (contentTypes.includes(ModuleContentTypes.ANY) || contentTypes.includes(ModuleContentTypes.LANDING_PAGE) || contentTypes.includes(ModuleContentTypes.SITE_PAGE) || contentTypes.includes(ModuleContentTypes.BLOG_POST) || contentTypes.includes(ModuleContentTypes.BLOG_LISTING)) : hostTemplateTypes && (hostTemplateTypes.includes(HOST_TEMPLATE_TYPES.ANY) || hostTemplateTypes.includes(HOST_TEMPLATE_TYPES.PAGE) || hostTemplateTypes.includes(HOST_TEMPLATE_TYPES.BLOG_POST) || hostTemplateTypes.includes(HOST_TEMPLATE_TYPES.BLOG_LISTING));
  }
  onChange(e) {
    const {
      onFieldChange
    } = this.props;
    onFieldChange(e.target.value);
  }
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      this.props.clearCache();
      offset = 0;
    }
    const query = {
      label__icontains: input,
      limit: DEFAULT_PAGE_SIZE,
      offset
    };
    this.props.fetchHubDbTables(query).then(response => {
      const {
        total
      } = response;
      callback(null, {
        options: this.props.options,
        pagination: {
          hasMore: total > this.props.options.length,
          offset: offset + DEFAULT_PAGE_SIZE,
          length: total
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  renderOption(option) {
    const optionLabel = /*#__PURE__*/_jsx(View, {
      marginInline: "400",
      paddingBlock: "200",
      as: "div",
      children: option.dropdownText || option.text
    });
    if (option.disabled) {
      return /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('unpublishedText')
        }),
        children: optionLabel
      });
    }
    return optionLabel;
  }
  renderPermissionsWarning() {
    const {
      isInRowField
    } = this.props;
    // global content is excluded from this check
    let titleTextKey;
    let bodyKey;
    if (!this.hasHubDbAccess && isInRowField) {
      titleTextKey = i18nKey('missingPermissionsTitle');
      bodyKey = i18nKey('missingPermissionsText');
    } else if (this.isMissingEmailPermissions) {
      titleTextKey = i18nKey('missingScopeTitle');
      bodyKey = i18nKey('missingScopeEmailText');
    } else if (this.isMissingContentPermissions) {
      titleTextKey = i18nKey('missingScopeTitle');
      bodyKey = i18nKey('missingScopeCMSText');
    }
    if (bodyKey && titleTextKey) {
      return /*#__PURE__*/_jsx(UIAlert, {
        className: "m-top-2",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: titleTextKey
        }),
        type: "warning",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: bodyKey
        })
      });
    }
    return null;
  }
  renderUnpublishedTableWarning() {
    const {
      isInRowField,
      isUnpublished
    } = this.props;
    if (!isUnpublished || isInRowField) return null;
    return /*#__PURE__*/_jsx(UIAlert, {
      className: "m-top-2",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('unpublishedTitle')
      }),
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('unpublishedText')
      })
    });
  }
  render() {
    const _this$props = this.props,
      {
        isDeleted,
        isUnpublished,
        readOnly = false,
        required,
        value
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const isInputReadOnly = readOnly || this.isMissingEmailPermissions || !this.hasHubDbAccess || this.isMissingContentPermissions;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(AsyncSelector, Object.assign({
        clearable: !required,
        "data-selenium": "hubdb-select",
        error: isDeleted || isUnpublished || undefined,
        loadOptions: this.debouncedLoadOptions,
        onChange: this.onChange,
        optionRenderer: this.renderOption,
        readOnly: isInputReadOnly,
        value: value
      }, rest)), isDeleted && /*#__PURE__*/_jsx(Small, {
        use: "error",
        weight: "medium",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('deletedError')
        })
      }), this.renderPermissionsWarning(), this.renderUnpublishedTableWarning()]
    });
  }
}
export default connector(HubDbField);